import { useState, useEffect } from "react";
import { Provider } from "react-redux";
import MainRoutes from "./routes";
import { store } from "./redux/storeOf";
import "./App.css";
import { Result, Button, Card, Row, Col, Space, Tag, Divider, ConfigProvider } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useAxiosSessionInterceptor } from "./utils/useAxiosSessionInterceptor";
import { useIdleTimer } from "react-idle-timer";

const PLATFORM_REQUIREMENTS = {
  macOS: {
    viewport: { width: 1440, height: 900 },
    scaled: { width: 1440, height: 900 },
    description: 'MacBook 13" or larger',
  },
  windows: {
    viewport: { width: 1366, height: 600 },
    scaled: { width: 1366, height: 600 },
    description: "Standard HD laptop or larger",
  },
  default: {
    viewport: { width: 1280, height: 600 },
    scaled: { width: 1280, height: 600 },
    description: "Standard desktop display",
  },
};

function App() {
  useAxiosSessionInterceptor();
  const [isResolutionSufficient, setIsResolutionSufficient] = useState(true);
  const [debugInfo, setDebugInfo] = useState(null);

  const checkResolution = () => {
    const dpr = window.devicePixelRatio || 1;

    // viewport for CSS pixels
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // OS-scaled / Hi-DPI effective resolution
    const scaledW = Math.round(vw * dpr);
    const scaledH = Math.round(vh * dpr);

    // Physical available screen space
    const sw = window.screen?.width;
    const sh = window.screen?.height;

    // Determine platform baseline
    const isMac = navigator.platform.toUpperCase().includes("MAC");
    const isWindows = navigator.platform.toUpperCase().includes("WIN");

    let platform = "default";
    if (isMac) platform = "macOS";
    else if (isWindows) platform = "windows";

    const requirements = PLATFORM_REQUIREMENTS[platform];

    console.log(`Platform detected: ${platform}`);
    console.log(
      `DPR: ${dpr}, VW: ${vw}, VH: ${vh}, ScaledW: ${scaledW}, ScaledH: ${scaledH}, SW: ${sw}, SH: ${sh}`,
    );

    const meetsViewportReq =
      vw >= requirements.viewport.width && vh >= requirements.viewport.height;

    const meetsScaledReq =
      scaledW >= requirements.scaled.width &&
      scaledH >= requirements.scaled.height;

    const meetsScreenReq =
      sw >= requirements.scaled.width && sh >= requirements.scaled.height;

    const ok = meetsViewportReq && meetsScreenReq;
    // const ok = meetsViewportReq && meetsScaledReq && meetsScreenReq;
    console.log(meetsViewportReq, meetsScaledReq, meetsScreenReq, ok);
    setIsResolutionSufficient(ok);

    setDebugInfo({
      vw,
      vh,
      dpr,
      scaledW,
      scaledH,
      sw,
      sh,
      meetsViewportReq,
      // meetsScaledReq,
      meetsScreenReq,
      platform,
      requirements,
    });
  };

  useEffect(() => {
    checkResolution();

    window.addEventListener("resize", checkResolution);
    window.addEventListener("orientationchange", checkResolution);

    // Browser zoom affects viewport metrics differently
    window
      .matchMedia("(resolution)")
      .addEventListener("change", checkResolution);

    return () => {
      window.removeEventListener("resize", checkResolution);
      window.removeEventListener("orientationchange", checkResolution);
      window
        .matchMedia("(resolution)")
        .removeEventListener("change", checkResolution);
    };
  }, []);

  // BEGIN IDLE LOGIC

  const handleOnIdle = (event) => {
    console.log("last active", getLastActiveTime());

    window.location.href = "/sign-in";
  };

  const handleOnActive = (event) => {
    console.log("user is active", event);
    console.log("time remaining", getRemainingTime());
  };

  const handleOnAction = (event) => {
    console.log("user did something", event);
  };
  const { getRemainingTime, getLastActiveTime } = useIdleTimer({
    timeout: 300_000,
    onIdle: handleOnIdle,
    onActive: handleOnActive,
    onAction: handleOnAction,
    debounce: 250,
  });

  return (
    <Provider store={store}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#15CCAE",
            colorPrimaryHover: "#4FECA0",
          },
          components: {
            Button: {
              colorTextLightSolid: "#000000",
            },
          },
        }}
      >
        <MainRoutes />
      </ConfigProvider>
    </Provider>
  );
}

export default App;
